import React, { useEffect, useRef, useState } from 'react';
import useGlobal from '../../../store';
import numberSpaces from '../../../scripts/helpers/numbers.with.spaces';
import Period, {

  STATS_PERIOD,
  currentMonthBounds,
  initialPeriodDates,

} from './period';
import './company.stats.scss';

function formatProfit( value ) {

  if ( value === null || value === undefined || value === '' || Number( value ) === 0 ) {

    return '0';

  }

  return numberSpaces( value ) || '0';

}

function formatCount( value ) {

  if ( value === null || value === undefined || value === '' ) { return '0'; }

  return String( value );

}

function formatLastDate( value ) {

  if ( !value ) { return '-'; }

  return value;

}

function formatConversion( contracts, projects ) {

  const total = Number( projects );
  const signed = Number( contracts );

  if ( !Number.isFinite( total ) || total <= 0 ) { return '0%'; }
  if ( !Number.isFinite( signed ) || signed <= 0 ) { return '0%'; }

  return `${ Math.round( ( signed / total ) * 100 ) }%`;

}

const CompanyStats = () => {

  const [ globalState, globalActions ] = useGlobal();
  const { user_id, user_role } = globalState;
  const { stats } = globalActions;

  const role = Number( user_role );
  const canSee = Boolean( user_id ) && ( role === 1 || role === 2 );

  const dates = initialPeriodDates();

  const [ periodType, setPeriodType ] = useState( STATS_PERIOD.ALL );
  const [ startDate, setStartDate ] = useState( dates.startDate );
  const [ endDate, setEndDate ] = useState( dates.endDate );
  const [ calendarOpen, setCalendarOpen ] = useState( false );

  const [ loading, setLoading ] = useState( false );
  const [ loadedOnce, setLoadedOnce ] = useState( false );
  const [ error, setError ] = useState( '' );
  const [ values, setValues ] = useState( null );

  const requestIdRef = useRef( 0 );
  const loadedForUserRef = useRef( null );

  async function fetchStats( nextPeriod, nextStart, nextEnd ) {

    if ( !canSee ) { return; }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setLoading( true );
    setError( '' );

    const result = await stats.load({

      periodType: nextPeriod,
      startDate: nextStart,
      endDate: nextEnd,

    });

    if ( requestId !== requestIdRef.current ) { return; }

    setLoading( false );

    if ( !result.success ) {

      setError( result.text || 'Не удалось загрузить статистику' );
      return;

    }

    setValues( result.stats );
    setLoadedOnce( true );

  }

  useEffect( () => {

    if ( !canSee ) {

      loadedForUserRef.current = null;
      return;

    }

    if ( loadedForUserRef.current === user_id ) { return; }

    loadedForUserRef.current = user_id;

    const initial = initialPeriodDates();

    setPeriodType( STATS_PERIOD.ALL );
    setStartDate( initial.startDate );
    setEndDate( initial.endDate );
    setCalendarOpen( false );
    setValues( null );
    setLoadedOnce( false );
    setError( '' );

    fetchStats( STATS_PERIOD.ALL, initial.startDate, initial.endDate );

  }, [ canSee, user_id ] );

  function handleSelectAll() {

    setPeriodType( STATS_PERIOD.ALL );
    fetchStats( STATS_PERIOD.ALL, startDate, endDate );

  }

  function handleSelectMonth() {

    const month = currentMonthBounds();

    setPeriodType( STATS_PERIOD.MONTH );
    setStartDate( month.startDate );
    setEndDate( month.endDate );
    fetchStats( STATS_PERIOD.MONTH, month.startDate, month.endDate );

  }

  function handleSelectCustom( nextStart, nextEnd ) {

    setPeriodType( STATS_PERIOD.CUSTOM );
    setStartDate( nextStart );
    setEndDate( nextEnd );
    fetchStats( STATS_PERIOD.CUSTOM, nextStart, nextEnd );

  }

  function handleRetry() {

    if ( !endDate && periodType !== STATS_PERIOD.ALL ) { return; }

    fetchStats( periodType, startDate, endDate );

  }

  if ( !canSee ) { return null; }

  const showLoadingText = loading && !loadedOnce && !values;
  const showMetrics = Boolean( values );

  return (

    <section className = "company-stats">

      <div className = "company-stats__header">

        {/* <h2 className = "company-stats__title">Статистика</h2> */}

        <Period

          periodType = { periodType }
          startDate = { startDate }
          endDate = { endDate }
          calendarOpen = { calendarOpen }
          setCalendarOpen = { setCalendarOpen }
          onSelectAll = { handleSelectAll }
          onSelectMonth = { handleSelectMonth }
          onSelectCustom = { handleSelectCustom }

        />

      </div>

      { error &&

        <div className = "company-stats__error">

          <p>{ error }</p>

          <button
            type = "button"
            className = "company-stats__retry"
            onClick = { handleRetry }
          >

            Повторить

          </button>

        </div>

      }

      { showLoadingText &&

        <p className = "company-stats__loading">Загрузка...</p>

      }

      { showMetrics &&

        <div className = "company-stats__metrics">

          {/* <div className = "company-stats__metric">

            <p className = "company-stats__metric-label">Дата крайнего замера:</p>
            <p className = "company-stats__metric-value company-stats__metric-value--date">
              { formatLastDate( values.project_last_date ) }
            </p>

          </div> */}

          <div className = "company-stats__metric">

            <p className = "company-stats__metric-label">Всего заключено договоров на сумму:</p>
            <p className = "company-stats__metric-value company-stats__metric-value--price">
              { formatProfit( values.profit ) }
            </p>

          </div>

          <div className = "company-stats__metrics-row">

            <div className = "company-stats__metric">

              <p className = "company-stats__metric-label">Замеров:</p>
              <p className = "company-stats__metric-value">
                { formatCount( values.projects ) }
              </p>

            </div>

            <div className = "company-stats__metric">

              <p className = "company-stats__metric-label">Договоров:</p>
              <p className = "company-stats__metric-value">
                { formatCount( values.contracts ) }
              </p>

            </div>

            { !(values.contracts > 0 && values.projects < 1) &&
              
              <div className = "company-stats__metric">

                <p className = "company-stats__metric-label">Конверсия:</p>
                <p className = "company-stats__metric-value">
                  { formatConversion( values.contracts, values.projects ) }
                </p>

              </div>
              
            }

          </div>

        </div>

      }

    </section>

  );

};

export default CompanyStats;
